import classNames from "classnames"

import { getGatsbyImage } from "../../../plugins/storyblok-image-plugin/src"
import { transformAssetSrc } from "../../../plugins/storyblok-image-plugin/src/utils/transformAssetUrl"
import GatsbyStoryblokImage from "../elements/V2/GatsbyStoryblokImage"

import { isMobile } from "@utils/V2/screen"
import React, { useRef, useState, useEffect, useCallback } from "react"

function isSafari(): boolean {
  const userAgent = globalThis.navigator?.userAgent ?? ""
  return userAgent.includes("Safari") && !userAgent.includes("Chrome")
}

// Safari on macOS Big Sur+ cannot seek webm videos via currentTime — must reload instead.
function resetVideoPlayback(
  videoElement: HTMLVideoElement,
  isWebm: boolean
): void {
  if (isSafari() && isWebm) {
    videoElement.load()
  } else {
    videoElement.currentTime = 0
  }
}

function getExternalVideoId(url: string): string {
  if (url.includes("youtube")) {
    if (url.includes("/shorts/")) return url.split("/shorts/")[1].split("?")[0]
    return url.split("?v=")[1].split("&")[0]
  }
  // youtu.be — extract last path segment
  return url.substring(url.lastIndexOf("/") + 1).split("&")[0]
}

function buildEmbedUrl(videoId: string, playbackMode: string): string {
  const autoplay = playbackMode === "autoplay"
  const notManual = playbackMode !== "manual"

  const params = new URLSearchParams({
    playsinline: "1",
    rel: "0",
    version: "3",
    // Required identifier for the YouTube JS API used by postMessage in hover mode.
    playerapiid: "ytplayer",
  })
  if (autoplay) params.set("autoplay", "1")
  if (notManual) {
    params.set("controls", "0")
    // enablejsapi enables postMessage commands (play/pause) used in hover mode.
    params.set("enablejsapi", "1")
    params.set("loop", "1")
    // YouTube's loop param only works when playlist is also set to the same video ID.
    params.set("playlist", videoId)
    params.set("mute", "1")
  }
  return `https://www.youtube.com/embed/${videoId}?${params}`
}

const ASPECT_CLASSES: Record<string, string> = {
  "1:1": "aspect-square",
  "4:3": "aspect-[4/3]",
  "16:9": "aspect-video",
  "3:4": "aspect-[3/4]",
  "9:16": "aspect-[9/16]",
}

function getAspectClass(aspectRatio: string, native = false): string {
  if (aspectRatio === "fullWidth")
    return native ? "aspect-auto" : "aspect-video"
  return ASPECT_CLASSES[aspectRatio] ?? ""
}

interface DefaultVideoProps {
  video: string
  coverImage: string
  ImageAlt?: string
  videoTitle?: string
  width?: number
  loadType?: "eager" | "lazy"
  fetchPriority?: "high" | "low" | "auto"
  sizes?: string
  srcSetWidths?: number[]
  rounded?: boolean
  aspectRatio: "1:1" | "16:9" | "9:16" | "3:4" | "4:3" | "fullWidth"
  playOnMobile?: boolean
  className?: string
}

interface AutoplayVideoProps extends DefaultVideoProps {
  playbackMode: "autoplay"
  pause?: boolean
  reset?: boolean
  preventLoop?: boolean
}

interface HoverVideoProps extends DefaultVideoProps {
  playbackMode: "hover"
  /*
    Note: The following prop should only be used in the case where
    Video is being used as a background to content and playback mode
    is "hover".

    The prop will allow for an external state to dictate whether the
    Video is being hovered or not.
  */
  videoContainerHover?: boolean
  preventLoop?: boolean
  resetOnLeave?: boolean
}

interface ManualVideoProps extends DefaultVideoProps {
  playbackMode: "manual"
  preventLoop?: undefined
}

type Props = AutoplayVideoProps | HoverVideoProps | ManualVideoProps

const Video = ({
  video,
  coverImage,
  ImageAlt,
  videoTitle,
  width,
  loadType,
  fetchPriority,
  sizes,
  srcSetWidths,
  rounded,
  aspectRatio,
  playbackMode,
  className,
  playOnMobile = true,
  preventLoop,
  ...props
}: Props) => {
  const accessibleVideoTitle = videoTitle?.trim() || ImageAlt?.trim() || "Video"
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  // useRef instead of useState — playing is never read in JSX, only in event handlers.
  // Avoids a re-render on every hover in/out.
  const playing = useRef(false)
  // true on SSR so the server always renders the cover image (video can't run server-side).
  // Flips to false on the client once we confirm the device should show video.
  const [useFallbackImage, setUseFallbackImage] = useState(true)

  // Destructure mode-specific props once to avoid scattered unsafe casts throughout the component
  const pause =
    playbackMode === "autoplay"
      ? (props as AutoplayVideoProps).pause
      : undefined
  const reset =
    playbackMode === "autoplay"
      ? (props as AutoplayVideoProps).reset
      : undefined
  const resetOnLeave =
    playbackMode === "hover"
      ? (props as HoverVideoProps).resetOnLeave
      : undefined
  const videoContainerHover =
    playbackMode === "hover"
      ? (props as HoverVideoProps).videoContainerHover
      : undefined

  useEffect(() => {
    if (playOnMobile || !isMobile()) {
      setUseFallbackImage(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCanPlay = useCallback(() => {
    if (playbackMode !== "autoplay") return
    playing.current = true
    if (iframeRef.current) {
      iframeRef.current.classList.add("opacity-100")
      // Default parameter captures iframeRef.current at call time so the callback
      // still has a valid reference if the component unmounts before 300ms elapses.
      setTimeout((iframeElement = iframeRef.current) => {
        // Remove the cover image from layout once the opacity fade-in completes.
        // previousElementSibling is the GatsbyStoryblokImage rendered just above the iframe.
        iframeElement.previousElementSibling?.classList.add("!hidden")
      }, 300)
    }
  }, [playbackMode])

  // Reset to frame 0 on end to prevent browsers from showing native playback UI in ended state.
  const handleEnded = useCallback(() => {
    if (!videoRef.current || playbackMode === "manual") return
    videoRef.current.currentTime = 0
  }, [playbackMode])

  const handleMouseEnter = useCallback(() => {
    if (playbackMode !== "hover" || playing.current) return
    if (videoRef.current) {
      videoRef.current.play()
    } else if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        "*"
      )
    }
    playing.current = true
  }, [playbackMode])

  const handleMouseLeave = useCallback(() => {
    if (playbackMode !== "hover" || !playing.current) return
    if (videoRef.current) {
      videoRef.current.pause()
      if (resetOnLeave) {
        resetVideoPlayback(videoRef.current, video.includes(".webm"))
      }
    } else if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        "*"
      )
    }
    playing.current = false
  }, [video, playbackMode, resetOnLeave])

  // Trigger hover handlers when videoContainerHover is driven by an external parent state
  useEffect(() => {
    if (videoContainerHover === undefined) return
    if (videoContainerHover) handleMouseEnter()
    else handleMouseLeave()
  }, [videoContainerHover, handleMouseEnter, handleMouseLeave])

  // Autoplay pause / reset control (driven by the pause and reset props)
  useEffect(() => {
    if (!videoRef.current || playbackMode !== "autoplay") return

    if (reset) {
      resetVideoPlayback(videoRef.current, video.includes(".webm"))
    }

    if (pause) {
      videoRef.current.pause()
    } else if (videoRef.current.readyState === 4) {
      // readyState 4 = HAVE_ENOUGH_DATA — safe to resume without buffering.
      videoRef.current.play()
    }
  }, [pause, reset, playbackMode, video])

  if (useFallbackImage) {
    return (
      <GatsbyStoryblokImage
        image={coverImage}
        alt={ImageAlt || "Video placeholder image"}
        loadType="eager"
        fetchPriority={fetchPriority}
        sizes={sizes}
        srcSetWidths={srcSetWidths}
        quality={10}
        aspectRatio={aspectRatio}
        rounded={rounded}
        className={classNames("relative w-full overflow-hidden", className)}
      />
    )
  }

  const isExternal = video.includes("youtube") || video.includes("youtu.be")

  if (isExternal) {
    const videoId = getExternalVideoId(video)
    const url = buildEmbedUrl(videoId, playbackMode)
    const containerAspectClass = getAspectClass(aspectRatio)
    const needsLetterboxOverscan =
      playbackMode !== "manual" && containerAspectClass === "aspect-video"
    const iframeHeightClass = needsLetterboxOverscan
      ? "h-[calc(100%+2px)]"
      : "h-full"

    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={
          aspectRatio !== "fullWidth" && width
            ? { maxWidth: `${width}px` }
            : undefined
        }
        className={classNames(
          "relative w-full overflow-hidden",
          // translate-x-0 triggers GPU compositing, which is required for overflow-hidden
          // to correctly clip corners in Safari when rounded is true.
          { "translate-x-0 overflow-hidden rounded": rounded },
          containerAspectClass,
          className
        )}
      >
        {playbackMode === "autoplay" && (
          <GatsbyStoryblokImage
            image={coverImage}
            alt={ImageAlt || "Video placeholder image"}
            loadType="eager"
            fetchPriority={fetchPriority}
            sizes={sizes}
            srcSetWidths={srcSetWidths}
            quality={10}
            aspectRatio={aspectRatio}
            rounded={rounded}
          />
        )}
        <iframe
          ref={iframeRef}
          title={accessibleVideoTitle}
          src={url}
          allow="autoplay; encrypted-media"
          allowFullScreen
          loading={loadType}
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleCanPlay}
          className={classNames(
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 object-cover object-center",
            iframeHeightClass,
            {
              "opacity-0 transition-opacity duration-300 ease-linear":
                playbackMode === "autoplay",
            },
            // Non-manual: oversized to fill the container without letterboxing.
            // pointer-events-none blocks right-click and native browser video controls.
            playbackMode === "manual"
              ? "w-full"
              : "pointer-events-none w-[300%]"
          )}
        />
      </div>
    )
  }

  const optimizedCoverImage = getGatsbyImage(coverImage, {
    width: width ?? 666,
    quality: 80,
    layout: "constrained",
  })

  return (
    <video
      ref={videoRef}
      aria-label={accessibleVideoTitle}
      width={width}
      poster={optimizedCoverImage.images.fallback?.src}
      autoPlay={playbackMode === "autoplay"}
      playsInline
      preload={
        loadType === "eager"
          ? "auto"
          : playbackMode === "hover"
            ? "metadata"
            : "none"
      }
      loop={playbackMode !== "manual" && preventLoop !== true}
      muted={playbackMode !== "manual"}
      controls={playbackMode === "manual"}
      onCanPlay={handleCanPlay}
      onEnded={handleEnded}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={
        aspectRatio !== "fullWidth" && width
          ? { maxWidth: `${width}px` }
          : undefined
      }
      className={classNames(
        "inline-block w-full object-cover object-center",
        // translate-x-0 triggers GPU compositing, which is required for overflow-hidden
        // to correctly clip corners in Safari when rounded is true.
        { "translate-x-0 overflow-hidden rounded": rounded },
        getAspectClass(aspectRatio, true),
        className
      )}
    >
      {/*
       * VP9 webm is required for correct playback on Safari macOS Big Sur+.
       * See: https://caniuse.com/webm
       */}
      <source
        src={transformAssetSrc(video)}
        type={video.includes(".webm") ? "video/webm" : "video/mp4"}
      />
      Your browser does not support HTML video.
    </video>
  )
}

export default Video
